import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

interface MetricInfo {
  id: string;
  name: string;
  category: string;
  growth_pillar_id: string;
  pillar_name?: string;
}

interface MetricLookupResult {
  metricsMap: Map<string, MetricInfo>;
  getMetricName: (idOrText: string) => string;
  getMetricInfo: (idOrText: string) => MetricInfo | null;
  isLoading: boolean;
  allMetrics: MetricInfo[];
}

// Helper to check if a string is a UUID
const isUUID = (str: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

export const useMetricLookup = (salesChannelId?: string): MetricLookupResult => {
  const [metricsMap, setMetricsMap] = useState<Map<string, MetricInfo>>(new Map());
  const [allMetrics, setAllMetrics] = useState<MetricInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchMetrics = async () => {
      if (!salesChannelId) {
        setMetricsMap(new Map());
        setAllMetrics([]);
        return;
      }

      setIsLoading(true);
      try {
        // First get pillars for this sales channel
        const { data: pillars, error: pillarsError } = await supabase
          .from('growth_pillars')
          .select('id, name')
          .eq('sales_channel_id', salesChannelId);

        if (pillarsError) throw pillarsError;

        if (!pillars || pillars.length === 0) {
          setMetricsMap(new Map());
          setAllMetrics([]);
          return;
        }

        const pillarIds = pillars.map(p => p.id);
        const pillarMap = new Map(pillars.map(p => [p.id, p.name]));

        // Then get all metrics for these pillars
        const { data: metrics, error: metricsError } = await supabase
          .from('growth_metrics')
          .select('id, name, category, growth_pillar_id')
          .in('growth_pillar_id', pillarIds);

        if (metricsError) throw metricsError;

        const map = new Map<string, MetricInfo>();
        const metricsList: MetricInfo[] = [];
        
        (metrics || []).forEach(m => {
          const info: MetricInfo = {
            id: m.id,
            name: m.name,
            category: m.category,
            growth_pillar_id: m.growth_pillar_id,
            pillar_name: pillarMap.get(m.growth_pillar_id)
          };
          map.set(m.id, info);
          metricsList.push(info);
        });

        setMetricsMap(map);
        setAllMetrics(metricsList);
      } catch (error) {
        console.error('Error fetching metrics for lookup:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMetrics();
  }, [salesChannelId]);

  const getMetricName = useCallback((idOrText: string): string => {
    if (!idOrText) return '';
    
    // If it's a UUID, look it up
    if (isUUID(idOrText)) {
      const metric = metricsMap.get(idOrText);
      if (metric) {
        return `${metric.name} (${metric.category})`;
      }
      return 'Unknown Metric';
    }
    
    // Otherwise it's legacy text, return as-is
    return idOrText;
  }, [metricsMap]);

  const getMetricInfo = useCallback((idOrText: string): MetricInfo | null => {
    if (!idOrText) return null;
    
    if (isUUID(idOrText)) {
      return metricsMap.get(idOrText) || null;
    }
    
    // For legacy text, try to find by name match
    for (const metric of metricsMap.values()) {
      if (idOrText.includes(metric.name)) {
        return metric;
      }
    }
    
    return null;
  }, [metricsMap]);

  return {
    metricsMap,
    getMetricName,
    getMetricInfo,
    isLoading,
    allMetrics
  };
};
